import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import { ActionBtn, ACTION_ACCENTS } from './shared/ActionBtn';
import TimelineAdminOverview, { TimelineGroup } from '../../component/TimelineAdminOverview';
import { Milestone, MilestoneStore, MILESTONE_STATUSES } from '../../store/planning';
import { Project, ProjectStore } from '../../store/Project';
import MilestoneEditPanel from './MilestoneEditPanel';
import { t } from '../../i18n';
import { OutlineIcon } from './shared/OutlineIcon';

const STATUS_COLORS: Record<string, string> = {
    planning: '#805ad5',
    active: '#3182ce',
    completed: '#38a169',
    on_hold: '#dd6b20',
};

const STATUS_LABELS: Record<string, string> = {
    planning: 'Planning',
    active: 'Active',
    completed: 'Completed',
    on_hold: 'On Hold',
};

@observer
export default class ProjectDetailOverview extends TimelineAdminOverview {
    myFilterKey = 'planning-project-detail';

    // R-022: Main store is MilestoneStore (planning). [T54191] The linked
    // budget rides along so a wrong (foreign-customer) link stays visible
    // and clearable in the edit panel. The customer for the panel's
    // budget picker comes from this screen's projectStore (not a
    // project.company relation — binder permission-checks every `with`
    // relation and not every planner may view companies).
    store = new MilestoneStore({ relations: ['project', 'budget'], limit: false });

    // Auxiliary stores
    projectStore = new ProjectStore({ relations: ['company'], limit: false });

    // The project ID from the route
    get projectId(): number {
        return Number((this.props as any).match?.params?.id);
    }

    /** [T54191] This project's customer — scopes the milestone edit
     * panel's budget picker to this customer's budgets. */
    get projectCompanyId(): number | null {
        const proj: any = this.projectStore.models.find((p: any) => p.id === this.projectId);
        return proj?.company?.id ?? null;
    }

    // R-029: Title shows project name
    @observable projectName = '';
    @observable title = t('nav.planning.projectDetail', 'Project Detail');

    // ── Modal state ──
    @observable editModalOpen = false;
    @observable editingMilestone: Milestone | null = null;
    @observable milestonePanelOpen = false;

    async componentDidMount() {
        // Fetch project name
        if (this.projectId) {
            try {
                await this.projectStore.fetch();
                const proj = this.projectStore.models.find((p: any) => p.id === this.projectId) as Project;
                if (proj) {
                    this.projectName = proj.name;
                    this.title = proj.name;
                }
            } catch (e) {
                console.warn('project fetch failed', e);
            }
        }
        await this.fetch();
    }

    // R-023: Store filtered by project ID
    async fetch() {
        try {
            await this.store.fetch().catch((e: any) => console.warn('milestone fetch failed', e));
        } catch (e) {
            console.error('fetch failed', e);
        }
    }

    get milestones(): Milestone[] {
        const all = this.store.models as Milestone[] || [];
        return all.filter((m: any) => {
            const projId = m.project?.id ?? m.project;
            return Number(projId) === this.projectId;
        });
    }

    // R-024: Left sidebar groups milestones (by status)
    @computed get timelineGroups(): TimelineGroup[] {
        const ms = this.milestones;
        const byStatus = new Map<string, Milestone[]>();

        for (const m of ms) {
            const status = (m as any).status || 'planning';
            if (!byStatus.has(status)) byStatus.set(status, []);
            (byStatus.get(status) as any[]).push(m);
        }

        // Sort by status order
        return MILESTONE_STATUSES
            .filter(s => byStatus.has(s))
            .map(status => ({
                id: status,
                label: STATUS_LABELS[status] || status,
                items: byStatus.get(status) as any[],
                color: STATUS_COLORS[status],
            }));
    }

    // R-025: Timeline shows milestone start/finish
    getBarStart(item: any): Date | null {
        const m = item as Milestone;
        if (m.startDate) return new Date(m.startDate);
        return null;
    }

    getBarEnd(item: any): Date | null {
        const m = item as Milestone;
        if (m.deadline) return new Date(m.deadline);
        if (m.startDate) return new Date(m.startDate);
        return null;
    }

    barColor(item: any): string {
        return STATUS_COLORS[(item as any).status] || '#3182ce';
    }

    getBarLabel(item: any): string {
        return (item as any).name || `#${item.id}`;
    }

    renderTimelineBar(item: any): React.ReactNode {
        const m = item as Milestone;
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name}
                    {m.deadline && ` · Due ${new Date(m.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                </span>
            </span>
        );
    }

    // Click milestone bar → open side panel
    onBarClick(item: any) {
        const m = item as Milestone;
        this.openEditMilestoneModal(m);
    }

    // R-026: Double-click milestone → detail
    onBarDoubleClick(item: any) {
        const milestoneId = item.id;
        if (milestoneId) {
            (this.props as any).history?.push(`/planning/milestone/${milestoneId}/overview`);
        }
    }

    // Navigate back to the project overview (customer view)
    @action backToCustomer = () => {
        (this.props as any).history?.push('/planning/project/overview');
    };

    // Back to customer (project overview) button
    renderBackButton(): React.ReactNode {
        return (
            <button
                data-test-back-to-customer
                onClick={this.backToCustomer}
                style={{
                    padding: '4px 12px', border: '1px solid #2185d0', borderRadius: 4,
                    background: '#e8f3fb', color: '#2185d0', cursor: 'pointer', fontSize: 13,
                    marginRight: 8, whiteSpace: 'nowrap',
                }}
            >
                ← Back to Customer
            </button>
        );
    }

    // R-028: Add milestone button
    @action addMilestone = async () => {
        if (!this.projectId) return;
        try {
            const api = (this.store as any).api;
            const ordering = this.milestones.length;
            await api.post('subproject/', {
                name: 'New Milestone',
                project: this.projectId,
                order: ordering,
                status: 'planning',
            });
            await this.store.fetch();
        } catch (e: any) {
            console.error('addMilestone error:', e);
        }
    };

    renderAddButton(): React.ReactNode {
        return (
            <ActionBtn
                data-test-add-milestone-btn
                onClick={this.addMilestone}
                accent={ACTION_ACCENTS.primary}
            >
                <OutlineIcon name="plus" color={ACTION_ACCENTS.primary} />
                <span>Add Milestone</span>
            </ActionBtn>
        );
    }

    // ── Modal actions ──
    @action openEditMilestoneModal = (milestone: Milestone) => {
        this.editingMilestone = milestone;
        this.milestonePanelOpen = true;
    };
    @action closeMilestonePanel = () => { this.milestonePanelOpen = false; this.editingMilestone = null; };
    @action afterMilestonePanelChange = async () => { await this.store.fetch(); };

    @action closeEditMilestoneModal = () => {
        this.editModalOpen = false;
        this.editingMilestone = null;
    };

    @action afterMilestoneSave = async () => {
        this.closeEditMilestoneModal();
        await this.store.fetch();
    };

    renderEditModal(): React.ReactNode { return null; }
    renderSidePanel(): React.ReactNode {
        return (
            <MilestoneEditPanel
                milestone={this.editingMilestone}
                visible={this.milestonePanelOpen}
                companyId={this.projectCompanyId}
                onClose={this.closeMilestonePanel}
                onChanged={this.afterMilestonePanelChange}
            />
        );
    }
}
